import type { DropResult } from '@hello-pangea/dnd';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment } from 'react';
import type { FieldErrors, UseFormReturn } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';

import { SectionContent } from '../../comments/section-content';
import { Section, SectionHeader } from '../components/Common';
import { CategoryListSkeleton } from '../components/SkeletonLoaders';
import { EditSkillsAndCompetenciesOptions } from './EditSkillsAndCompetenciesOptions';
import { SkillsSectionOptions } from './SkillsSectionOptions';
import { CategoryList } from './skills/CategoryList';
import type { CompetencyWithSkills, ProfileForm } from './types';

type SkillsSectionProps = {
    readonly aiSuggestions?: ReactNode;
    readonly architectureId: string;
    readonly commentable: boolean;
    readonly newThreadCreationDisabled?: boolean;
    readonly competenciesWithSkills: readonly CompetencyWithSkills[];
    readonly currentSkillCount: number;
    readonly formErrors: FieldErrors<ProfileForm>;
    readonly formMethods: UseFormReturn<ProfileForm>;
    readonly hiddenSkillIds?: ReadonlySet<string>;
    readonly isEditing: boolean;
    readonly isGeneratingSkills: boolean;
    readonly isSkillLimitReached: boolean;
    readonly itemId?: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly maximumSkillLimit: number | undefined;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onManualEdit: () => void;
    readonly onMove: (result: DropResult<string>) => void;
    readonly onOpenSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveCompetency: (competencyId: string) => void;
    readonly onRemoveSkill: (competencyId: string, skillId: string) => void;
    readonly showProficiency: boolean;
};

export const SkillsSection: FunctionComponent<SkillsSectionProps> = ({
    aiSuggestions,
    architectureId,
    commentable,
    newThreadCreationDisabled = false,
    competenciesWithSkills,
    currentSkillCount,
    formErrors,
    formMethods,
    hiddenSkillIds,
    isEditing,
    isGeneratingSkills,
    isSkillLimitReached,
    itemId,
    itemType,
    maximumSkillLimit,
    onChangeProficiencyLevel,
    onManualEdit,
    onMove,
    onOpenSkillDetails,
    onRemoveCompetency,
    onRemoveSkill,
    showProficiency,
}) => {
    const translate = useTranslate();

    return (
        <Section>
            <SectionHeader>
                {translate('pages.job-architecture-v2.job-profile-required-skills')}
                {currentSkillCount > 0 && (
                    <span className="text-body-s text-text-content-subtle tabular-nums">
                        <span aria-hidden="true">{currentSkillCount}</span>
                        <span className="sr-only">
                            {translate('pages.job-architecture-v2.job-profile-skill-count', {
                                count: currentSkillCount,
                            })}
                        </span>
                    </span>
                )}
                <SkillsSectionOptions />
                {isEditing && (
                    <div className="ml-auto">
                        <EditSkillsAndCompetenciesOptions
                            architectureId={architectureId}
                            control={formMethods.control}
                            currentSkillCount={currentSkillCount}
                            hiddenSkillIds={hiddenSkillIds}
                            isSkillLimitReached={isSkillLimitReached}
                            itemType={itemType}
                            maximumSkillLimit={maximumSkillLimit}
                            onCompetencyChange={onManualEdit}
                            onSkillChange={onManualEdit}
                        />
                    </div>
                )}
            </SectionHeader>
            <SectionContent>
                {isGeneratingSkills ? (
                    // biome-ignore lint/complexity/noUselessFragments: suppressed due to migration
                    <Fragment>
                        <CategoryListSkeleton />
                    </Fragment>
                ) : (
                    <Fragment>
                        {aiSuggestions}
                        <CategoryList
                            commentable={commentable}
                            competenciesWithSkills={competenciesWithSkills}
                            formErrors={formErrors}
                            handleOnDragEnd={onMove}
                            highlightSelectedSkill={true}
                            isInEditMode={isEditing}
                            itemId={itemId}
                            newThreadCreationDisabled={newThreadCreationDisabled}
                            showProficiency={showProficiency}
                            onChangeProficiencyLevel={onChangeProficiencyLevel}
                            onOpenSkillDetails={onOpenSkillDetails}
                            onRemoveCompetency={onRemoveCompetency}
                            onRemoveSkill={onRemoveSkill}
                            onSkillChange={onManualEdit}
                        />
                    </Fragment>
                )}
            </SectionContent>
        </Section>
    );
};
